import { t } from "@web-monorepo/i18n/macro";
import * as logClient from "@classdojo/log-client";
import { clsx } from "clsx";
import { useEffect, useReducer, useRef } from "react";
import { Timer } from "src/pods/timer";
import alarm from "src/pods/timer/assets/alarm.mp3";
import alarmCaptions from "src/pods/timer/assets/alarm.vtt?url";
import MaterialIcon from "src/shared/components/MaterialIcon";
import logEvent from "src/utils/logEvent";
import styles from "./TimerDisplay.module.css";

type TimerDisplayProps = {
  sendTime?: (time: Timer) => void;
  time?: Timer;
  fullscreen: boolean;
  shouldRingBell: boolean;
};

function computeSecondsRemaining(time: Timer | undefined): number {
  const { seconds, startTime, endTime } = time || { seconds: 0 };
  if (!seconds) return 0;
  return Math.max(Math.ceil(seconds - ((endTime || Date.now()) - (startTime || Date.now())) / 1000), 0);
}

function formatTime(seconds: number): string {
  if (seconds >= 60) return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  if (seconds > 0) return String(seconds);
  return t`Time's Up!`;
}

export default function TimerDisplay({ sendTime, time, fullscreen, shouldRingBell }: TimerDisplayProps) {
  // Tick driver — replaces the original class's forceUpdate() inside the
  // 100ms interval. Bumping the counter triggers a re-render.
  const [, tick] = useReducer((x: number) => x + 1, 0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const seconds = computeSecondsRemaining(time);

  // Latest-props sendTime so the interval's updateTime closure isn't stale.
  const latestProps = useRef({ time, sendTime, shouldRingBell });
  latestProps.current = { time, sendTime, shouldRingBell };

  const updateTime = () => {
    tick();
    const { time: latestTime, sendTime: latestSendTime, shouldRingBell: ring } = latestProps.current;
    if (computeSecondsRemaining(latestTime) > 0) return;

    // Clear the interval eagerly so the next 100ms tick can't fire and produce
    // a duplicate sendTime before the parent's re-render propagates the
    // endTime back through props and the effect cleanup runs.
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (audioRef.current && ring) {
      audioRef.current.play().catch(() => {
        logClient.sendMetrics([{ type: "increment", value: 1, metricName: "toolkit.media.audio.play.error" }]);
      });
    }
    // Finalize the timer by sending an endTime.
    const now = Date.now();
    const newTime = { ...latestTime, endTime: now };
    newTime.remainingTime =
      newTime.startTime && newTime.seconds ? newTime.seconds * 1000 - (now - newTime.startTime) : null;
    latestSendTime?.(newTime);
  };

  // Start/stop the high-frequency interval whenever timer-running state
  // changes. We sample at 100ms (not 1s) to prevent drift artifacts like
  // skipping seconds or pauses near the boundary.
  const isRunning = Boolean(time?.seconds && time?.startTime && !time?.endTime);
  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(updateTime, 100);
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
      };
    }
    return undefined;
  }, [isRunning]);

  useEffect(() => {
    logEvent({ eventName: "timer.result.open" });
    return () => {
      logEvent({ eventName: "timer.result.close" });
    };
  }, []);

  const sendTimeUpdate = (details: Partial<Timer>) => {
    const now = Date.now();
    const newTime = { ...time, ...details };
    newTime.remainingTime =
      newTime.startTime && newTime.seconds
        ? newTime.seconds * 1000 - ((newTime.endTime || now) - newTime.startTime)
        : null;
    sendTime?.(newTime);
  };

  const pauseTimer = () => {
    sendTimeUpdate({ startTime: time?.startTime, endTime: Date.now() });
  };

  const resumeTimer = (fromBeginning?: boolean) => {
    let { startTime, endTime } = time || {};
    if (fromBeginning || !startTime) {
      startTime = Date.now();
    } else if (startTime && endTime) {
      startTime = Date.now() - (endTime - startTime);
    }
    endTime = null;
    sendTimeUpdate({ startTime, endTime });
  };

  const toggleTimer = () => {
    if (computeSecondsRemaining(time) <= 0) return resumeTimer(true);
    if (isRunning) return pauseTimer();
    return resumeTimer();
  };

  const incrementTimer = (ms: number) => {
    const newStartTime = time?.startTime ? time.startTime + ms : null;
    sendTimeUpdate({ startTime: newStartTime, increment: ms });
    logEvent({ eventName: "timer.result.increment" });
  };

  const resetTimer = () => {
    sendTimeUpdate({ startTime: isRunning ? Date.now() : null, endTime: null, reset: true });
  };

  const isLowTime = seconds <= 3;
  const isCountingDown = isRunning && seconds > 0;
  const timeDisplay = formatTime(seconds);

  return (
    <section className={clsx(fullscreen && styles.pageFullscreen, fullscreen && isLowTime && styles.pageDanger)}>
      <div className={styles.timer}>
        <div
          className={clsx(styles.time, fullscreen ? styles.timeFullscreen : styles.timeRemote)}
          role="timer"
          aria-live="polite"
          aria-atomic="true"
        >
          {timeDisplay}
        </div>
        <audio src={alarm} ref={audioRef}>
          <track kind="captions" src={alarmCaptions} srcLang="en" label={t`English`} default />
        </audio>
        <footer className={styles.controls}>
          <button
            type="button"
            data-name="timer_display:render:material_icon"
            onClick={resetTimer}
            aria-label={t`Replay timer`}
            className={styles.button}
          >
            <MaterialIcon icon="replay" className={styles.sideIcon} />
          </button>
          <button
            type="button"
            data-name={
              isCountingDown
                ? "timer_display:render_timer_controls:pause_timer"
                : "timer_display:render_timer_controls:play_timer"
            }
            onClick={toggleTimer}
            className={styles.button}
            aria-label={isCountingDown ? t`Pause timer` : t`Play timer`}
          >
            <MaterialIcon
              icon={isCountingDown ? "pause" : "play_arrow"}
              className={clsx(styles.mainIcon, isLowTime && styles.mainIconLowTime)}
            />
          </button>
          <button
            type="button"
            data-name="timer_display:render:plus_ten"
            onClick={() => incrementTimer(10000)}
            aria-label={t`Add 10 seconds to timer`}
            className={styles.button}
          >
            <span className={styles.sideIcon}>+10</span>
          </button>
        </footer>
      </div>
    </section>
  );
}
